import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import Input from '@material-ui/core/Input';
import times from 'lodash/times';
import take from 'lodash/take';
import getPlainText from "utils/mention-to-plaintext";
import HelpContentModal from './HelpContentModal';

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: 'transparent',
    marginBottom: theme.spacing(5),
    borderRadius: 0,
    boxShadow: 'none',
  },
  tableContainer: {
    display:'flex',
    backgroundColor: 'white',
    borderRadius: 0,
    '& .MuiTableCell-head': {
      border: '1px solid #D2D7E3',
      borderBottomWidth: 2,
      fontSize: 12,
      textTransform: 'uppercase'
    },
    '& .MuiInputBase-multiline': {
      padding: 0
    },
    '& .MuiInputBase-input': {
      padding: 10,
      fontSize: 14,
    }
  },
  table: {
    border: '1px solid #D2D7E3',
    '& td': {
      border: '1px solid #D2D7E3',
    },
    borderCollapse: 'separate'
  },
  desc: {
    marginBottom: theme.spacing(2),
    whiteSpace: 'pre-wrap'
  },
  sticky: {
    position: "sticky",
    width: "5em",
    whiteSpace: 'pre',
    left: 0,
    '&:before': {
      content:'Row'
    },
    background:'#FAFBFD',
    zIndex:10
  }
}));

const TableRowsView = ({
  rows,
  columns,
  columnsCount,
  name,
  onChange,
  values,
  viewOnly,
  readOnly,
}) => {
  const classes = useStyles();
  columns = columnsCount ? take(columns, columnsCount) : [];
  const headers = columns.map((col, idx) => (
    <TableCell className={idx ===0 && classes.sticky} style={{ width: idx === 0 ? '20%' : 'auto' }} key={idx}>{col?.header}</TableCell>
  ));

  const handleChange = (e) => {
    const { value, name } = e.target;

    onChange && onChange(name, value);
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>{headers}</TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((r, i) => {
          return  (
            <TableRow key={i}>
              <TableCell className={classes.sticky}>{r.header}</TableCell>
              {take(columns, columnsCount - 1).map((col, idx) => (
                <React.Fragment>
                  <TableCell  padding={viewOnly ? 'default' : 'none'}>
                    {viewOnly ? (
                      values?.[i]?.[idx]
                    ) : (
                      <Input
                        style={{width:200}}
                        multiline
                        disableUnderline
                        fullWidth
                        key={idx}
                        margin="none"
                        name={`${name}.values.${i}.${idx}`}
                        value={values?.[i]?.[idx]}
                        onChange={handleChange}
                        readOnly={readOnly}
                        placeholder="Type here"
                      />
                    )}
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          )})}
        </TableBody>
      </Table>
     
    </TableContainer>
  );
};

const TableColumnsView = ({
  rowsCount,
  allowAddRows,
  columns,
  name,
  onChange,
  values,
  viewOnly,
  readOnly,
}) => {
  const classes = useStyles();
  const handleChange = (e) => {
    const { value, name } = e.target;
    onChange && onChange(name, value);
  };

  const onAddRow = () => {
    onChange && onChange(`${name}.rowsCount`, rowsCount + 1);
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            {columns?.map((col, idx) => (
              <TableCell key={idx}>{col?.header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {times(rowsCount).map((i, index) => (
            <TableRow key={i}>
              {columns?.map((c, idx) => (
                <TableCell  padding={viewOnly ? 'default' : 'none'}>
                  {viewOnly ? (
                    values?.[index]?.[idx]
                  ) : (
                    <Input
                      multiline
                      disableUnderline
                      fullWidth
                      key={idx}
                      margin="none"
                      value={values?.[index]?.[idx]}
                      name={`${name}.values.${index}.${idx}`}
                      onChange={handleChange}
                      readOnly={readOnly}
                      placeholder="Type here"
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
          { allowAddRows && !(viewOnly ||readOnly) && (
            <TableRow>
              <TableCell colSpan={columns?.length} align="center" style={{ backgroundColor: '#FAFBFD' }}>
                <Button
                  size="small"
                  onClick={onAddRow}
                  color="primary"
                  variant="text"
                  startIcon={<AddIcon />}
                >
                  Add one more row
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TableBlock = (props) => {
  const classes = useStyles();
  const {
    layout,
    rowsCount,
    columnsCount,
    columns,
    rows,
    description,
    name,
    helpContent,
    onChange,
    values,
    allowAddRows,
    viewOnly,
    readOnly,
  } = props;
  return (
    <Card className={classes.card}>
      {description && (
        <Typography variant="body2" color="textPrimary" className={classes.desc}>
          <HelpContentModal content={helpContent} label={getPlainText(description)} />
        </Typography>
      )}
      {layout === 'row' ? (
        <TableRowsView
          rows={rows}
          columns={columns}
          columnsCount={columnsCount}
          name={name}
          onChange={onChange}
          values={values}
          viewOnly={viewOnly}
          readOnly={readOnly}
        />
      ) : (
        <TableColumnsView
          rowsCount={rowsCount}
          columns={columns}
          name={name}
          onChange={onChange}
          values={values}
          viewOnly={viewOnly}
          allowAddRows={allowAddRows}
          readOnly={readOnly}
        />
      )}
    </Card>
  );
};

export default React.memo(TableBlock);
